import { useState, useEffect } from "react";
import { API, useAuth } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Shield, Trash2, Loader2,
  Crown, MoreHorizontal, Pencil, X, Check, Building, Mail
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator
} from "./ui/dropdown-menu";

const ROLE_LABELS = {
  super_admin: "Admin",
  coordinator: "Coordinator",
};

const ROLE_COLORS = {
  super_admin: { bg: "var(--t-primary)", text: "#fff" },
  coordinator: { bg: "#64748b", text: "#fff" },
};

const TeamManagement = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit org name
  const [editingName, setEditingName] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [membersRes, orgRes] = await Promise.all([
        axios.get(`${API}/org/members`, { withCredentials: true }),
        axios.get(`${API}/org/info`, { withCredentials: true }),
      ]);
      setMembers(membersRes.data.members || []);
      setPendingInvites(membersRes.data.pending_invites || []);
      setOrgInfo(orgRes.data);
      setOrgName(orgRes.data?.name || "");
    } catch {
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/org/members/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success("Role updated");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const handleRemoveMember = async (userId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from the team? They will lose access to all org data.`)) return;
    try {
      await axios.delete(`${API}/org/members/${userId}`, { withCredentials: true });
      toast.success(`${memberName} removed from team`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove member");
    }
  };

  const handleCancelInvite = async (email) => {
    try {
      await axios.delete(`${API}/org/invites/${encodeURIComponent(email)}`, { withCredentials: true });
      toast.success("Invite cancelled");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel invite");
    }
  };

  const handleSaveOrgName = async () => {
    if (!orgName.trim()) return;
    setSavingName(true);
    try {
      await axios.put(`${API}/org/info`, { name: orgName.trim() }, { withCredentials: true });
      toast.success("Organization name updated");
      setEditingName(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update name");
    } finally {
      setSavingName(false);
    }
  };

  if (loading) {
    return (
      <Card className="card-surface" data-testid="team-card">
        <CardContent className="p-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: "var(--t-text-muted)" }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-surface" data-testid="team-card">
      <CardHeader style={{ borderBottom: "1px solid var(--t-border)", padding: "16px 20px" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5" style={{ color: "var(--t-text-muted)" }} />
            <div>
              <CardTitle className="text-lg font-semibold" style={{ color: "var(--t-text)", fontFamily: "var(--t-heading-font)" }}>
                Team
              </CardTitle>
              <CardDescription style={{ color: "var(--t-text-muted)" }}>
                Manage members in your organization
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-5">
        {/* Org Name */}
        <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "var(--t-bg)", border: "1px solid var(--t-border)" }}>
          <Building className="w-5 h-5 flex-shrink-0" style={{ color: "var(--t-primary)" }} />
          {editingName ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="h-8 text-sm flex-1"
                autoFocus
                data-testid="org-name-input"
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveOrgName(); if (e.key === "Escape") setEditingName(false); }}
              />
              <Button size="sm" variant="ghost" onClick={handleSaveOrgName} disabled={savingName} data-testid="save-org-name">
                <Check className="w-4 h-4 text-green-600" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditingName(false); setOrgName(orgInfo?.name || ""); }}>
                <X className="w-4 h-4" style={{ color: "var(--t-text-muted)" }} />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm font-medium" style={{ color: "var(--t-text)" }}>
                {orgInfo?.name || "Your Organization"}
              </span>
              <button
                onClick={() => setEditingName(true)}
                className="p-1 rounded hover:bg-black/5 transition-colors"
                data-testid="edit-org-name-btn"
              >
                <Pencil className="w-3.5 h-3.5" style={{ color: "var(--t-text-muted)" }} />
              </button>
            </div>
          )}
        </div>

        {/* Members List */}
        <div className="space-y-1" data-testid="members-list">
          {members.map((m) => {
            const isOwner = m.user_id === orgInfo?.owner_user_id;
            const isSelf = m.user_id === user?.user_id;
            const roleStyle = ROLE_COLORS[m.role] || ROLE_COLORS.coordinator;

            return (
              <div
                key={m.user_id}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-black/[0.02] transition-colors"
                data-testid={`member-row-${m.user_id}`}
              >
                {m.picture ? (
                  <img src={m.picture} alt={m.name} className="w-9 h-9 rounded-full" />
                ) : (
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                    style={{ background: "var(--t-primary)" }}
                  >
                    {m.name?.charAt(0) || "?"}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate" style={{ color: "var(--t-text)" }}>
                      {m.name}
                    </span>
                    {isOwner && (
                      <Badge className="text-[9px] px-1.5 py-0" style={{ background: "#D97706", color: "#fff" }}>
                        Owner
                      </Badge>
                    )}
                    {isSelf && (
                      <span className="text-[10px] font-medium" style={{ color: "var(--t-text-muted)" }}>(you)</span>
                    )}
                  </div>
                  <span className="text-xs truncate block" style={{ color: "var(--t-text-muted)" }}>
                    {m.email}
                  </span>
                </div>
                <Badge
                  className="text-[10px] flex-shrink-0"
                  style={{ background: roleStyle.bg + "18", color: roleStyle.bg, border: `1px solid ${roleStyle.bg}30` }}
                >
                  {ROLE_LABELS[m.role] || m.role}
                </Badge>
                {!isSelf && !isOwner && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`member-actions-${m.user_id}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleChangeRole(m.user_id, m.role === "super_admin" ? "coordinator" : "super_admin")}
                      >
                        <Shield className="w-3.5 h-3.5 mr-2" />
                        Make {m.role === "super_admin" ? "Coordinator" : "Admin"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600 focus:text-red-600"
                        onClick={() => handleRemoveMember(m.user_id, m.name)}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            );
          })}
        </div>

        {/* Pending Invites */}
        {pendingInvites.length > 0 && (
          <div className="space-y-2" data-testid="pending-invites">
            <p className="text-xs font-medium uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>
              Pending Invites
            </p>
            {pendingInvites.map((inv) => (
              <div
                key={inv.email}
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{ background: "var(--t-bg)", border: "1px dashed var(--t-border)" }}
                data-testid={`invite-row-${inv.email}`}
              >
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--t-border)" }}>
                  <Mail className="w-4 h-4" style={{ color: "var(--t-text-muted)" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium truncate block" style={{ color: "var(--t-text)" }}>
                    {inv.name || inv.email}
                  </span>
                  <span className="text-xs truncate block" style={{ color: "var(--t-text-muted)" }}>
                    {inv.email} — Invited as {ROLE_LABELS[inv.role] || inv.role}
                  </span>
                </div>
                <Badge variant="outline" className="text-[10px] flex-shrink-0" style={{ color: "#D97706", borderColor: "#D97706" }}>
                  Pending
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                  onClick={() => handleCancelInvite(inv.email)}
                  data-testid={`cancel-invite-${inv.email}`}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

    </Card>
  );
};

export default TeamManagement;
